<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const { localeTag } = useDateLocale();

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const currencyFormatter = computed(() =>
  new Intl.NumberFormat(localeTag.value, { style: 'currency', currency: 'BRL' })
);
const numberFormatter = computed(() => new Intl.NumberFormat(localeTag.value));

const formatNumber = v => numberFormatter.value.format(Number(v) || 0);
const formatCurrency = v => currencyFormatter.value.format(Number(v) || 0);

const cards = computed(() => {
  if (!props.report) return [];
  const r = props.report;
  return [
    {
      key: 'delivered',
      label: t('FLOW_BUILDER.REPORTS.CARDS.DELIVERED_MESSAGES'),
      value: formatNumber(r.delivered_messages?.recent),
    },
    {
      key: 'revenue',
      label: t('FLOW_BUILDER.REPORTS.CARDS.REVENUE'),
      value: formatCurrency(r.orders_revenue?.recent),
    },
    {
      key: 'orders',
      label: t('FLOW_BUILDER.REPORTS.CARDS.ORDERS'),
      value: formatNumber(r.orders_count?.recent),
    },
    {
      key: 'recipients',
      label: t('FLOW_BUILDER.REPORTS.CARDS.UNIQUE_RECIPIENTS'),
      value: formatNumber(r.unique_recipients),
    },
  ];
});
</script>

<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    <template v-if="loading">
      <div
        v-for="n in 4"
        :key="`skeleton-${n}`"
        class="flex flex-col gap-2 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4 animate-pulse"
      >
        <div class="h-3 w-24 rounded bg-n-alpha-2" />
        <div class="h-7 w-20 rounded bg-n-alpha-2 mt-0.5" />
      </div>
    </template>
    <template v-else>
      <div
        v-for="card in cards"
        :key="card.key"
        class="flex flex-col gap-1.5 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
      >
        <span
          class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ card.label }}
        </span>
        <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
          {{ card.value }}
        </span>
      </div>
    </template>
  </div>
</template>
